import {
  Auth0ContextInterface,
  GetTokenSilentlyOptions,
} from "@auth0/auth0-react";
import React from "react";
import invariant from "tiny-invariant";
import { create } from "zustand";

import { isExpiredSessionError } from "@/api/errors";

type Fn = Auth0ContextInterface["getAccessTokenSilently"];
type LoginFn = Auth0ContextInterface["loginWithRedirect"];

type TokenRefresh = {
  pending: boolean;
  token: string | undefined;
  updateFn: ((options?: GetTokenSilentlyOptions) => Promise<void>) | undefined;
  setUpdateFn: (fn: Fn, login: LoginFn) => void;
};

let redirecting = false;

const useTokenStore = create<TokenRefresh>((set) => ({
  pending: false,
  setUpdateFn: (getFreshToken, login) =>
    set(() => ({
      updateFn: async (options) => {
        set({ pending: true });
        try {
          const token = await getFreshToken(options);
          set({
            token: token,
          });
        } catch (e) {
          if (isExpiredSessionError(e) && !redirecting) {
            redirecting = true;
            void login({ appState: { returnTo: window.location.href } });
          }
          throw e;
        } finally {
          set({ pending: false });
        }
      },
    })),
  token: undefined,
  updateFn: undefined,
}));

const useTokenUpdate = () => {
  const updateFn = useTokenStore((state) => state.updateFn);
  const pending = useTokenStore((state) => state.pending);
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(updateFn, "Token store has not been initialized");

  const forceUpdate = React.useCallback(async () => {
    await updateFn({ cacheMode: "off" });
  }, [updateFn]);

  return [pending, forceUpdate] as const;
};

export { useTokenStore, useTokenUpdate };
